import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { usePortalTooltip } from './use-portal-tooltip';

interface HoverTooltipProps {
  children: React.ReactNode;
  tooltip: string;
  portal?: boolean;
}

const Trigger = styled.span<{ portalled: boolean }>`
  cursor: help;
  position: relative;

  ${(props) =>
    !props.portalled &&
    `&:hover > span,
    &:focus > span {
      opacity: 1;
      visibility: visible;
    }`}
`;

const Bubble = styled.span<{ portalled: boolean }>`
  background: ${(props) => props.theme.colors.primaryBrand};
  border-radius: 6px;
  bottom: ${(props) => (props.portalled ? 'auto' : 'calc(100% + 8px)')};
  color: ${(props) => props.theme.colors.surface};
  font-size: 13px;
  font-weight: normal;
  line-height: 1.4;
  opacity: ${(props) => (props.portalled ? 1 : 0)};
  padding: 8px 10px;
  pointer-events: none;
  position: ${(props) => (props.portalled ? 'fixed' : 'absolute')};
  right: ${(props) => (props.portalled ? 'auto' : '0')};
  text-align: left;
  visibility: ${(props) => (props.portalled ? 'visible' : 'hidden')};
  white-space: normal;
  width: 240px;
  max-width: calc(100vw - 16px);
  z-index: ${(props) => (props.portalled ? 10000 : 2)};
`;

let nextTooltipId = 0;

export const HoverTooltip: React.FC<HoverTooltipProps> = ({ children, tooltip, portal = false }) => {
  const [id] = React.useState(() => `hover-tooltip-${++nextTooltipId}`);
  const { triggerRef, bubbleRef, isOpen, position, ...events } = usePortalTooltip(portal);
  const portalled = portal && isOpen;
  const bubble = (
    <Bubble
      ref={bubbleRef}
      id={id}
      role="tooltip"
      portalled={portalled}
      style={portalled ? position || { visibility: 'hidden' } : undefined}
    >
      {tooltip}
    </Bubble>
  );

  return (
    <Trigger ref={triggerRef} tabIndex={0} aria-describedby={id} portalled={portal} {...events}>
      {children}
      {portalled ? ReactDOM.createPortal(bubble, document.body) : bubble}
    </Trigger>
  );
};
